import React, { useState, useEffect } from "react";
import { CSpinner } from "@coreui/react-pro";
import { useNavigate, useParams } from "react-router-dom";
import { getOrder, createOrder } from "../../utils/backend";
import { Formik, Form, Field, ErrorMessage, FieldArray } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";

const CreateOrder = () => {
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const [insuranceProviderType, setInsuranceProviderType] = useState("select");

  const handleInsuranceProviderTypeChange = (event, setFieldValue) => {
    setInsuranceProviderType(event.target.value);
    setFieldValue(
      "insurer_name",
      event.target.value === "other" ? "" : event.target.value
    );
  };

  const [initialValues, setInitialValues] = useState({
    user_id: "",
    product_id: "",
    order_type: "inquiry",
    insurance_type: "",
    enquiry_id: "",
    order_number: "",
    full_name: "",
    username: "",
    insurance_plan: "",
    insurer_name: "",
    order_status: "",
    order_date: "",
    order_value: "",
    payment_status: "",
    tracking_number: "",
    order_notes: "",
    product_code: "",
    quote_id: "",
    selected_plan_code: "",
    quoted_amount: "",
    state: "",
    city: "",
    postcode: "",
    address_line_1: "",
    address_line_2: "",
    document_type: "",
    gender: "",
    email_address: "",
    date_of_birth: "",
    mobile_number: "",
    documentID: "",
    nationality: "",
    accept_terms: "",
    agree_declaration: "",
    road_tax_renewal: "",
    additional_fields: [],
    optional_benefits: [],
    nominees: [],
  });

  const validationSchema = Yup.object().shape({
    user_id: Yup.string().required("User ID is required"),
    insurance_type: Yup.number()
      .integer("Insurance type must be an integer")
      .required("Insurance type is required"),
    enquiry_id: Yup.string().required("Enquiry ID is required"),
    order_number: Yup.string().required("Order number is required"),
    full_name: Yup.string().required("Full name is required"),
    username: Yup.string().required("Username is required"),
    insurance_plan: Yup.string().required("Insurance plan is required"),
    insurer_name: Yup.string().required("Insurer name is required"),
    order_status: Yup.string().required("Order status is required"),
    order_date: Yup.date().required("Order date is required"),
    order_value: Yup.number().required("Order value is required"),
    payment_status: Yup.string().required("Payment status is required"),
    tracking_number: Yup.string().required("Tracking number is required"),
    order_notes: Yup.string().required("Order notes are required"),
    product_code: Yup.string().required("Product code is required"),
    quote_id: Yup.string().required("Quote ID is required"),
    selected_plan_code: Yup.string().required("Selected plan code is required"),
    quoted_amount: Yup.number().required("Quoted amount is required"),
    state: Yup.string().required("State is required"),
    city: Yup.string().required("City is required"),
    postcode: Yup.string().required("Postcode is required"),
    address_line_1: Yup.string().required("Address Line 1 is required"),
    address_line_2: Yup.string().required("Address Line 2 is required"),
    document_type: Yup.string().required("Document type is required"),
    gender: Yup.string().required("Gender is required"),
    email_address: Yup.string()
      .email("Invalid email")
      .required("Email is required"),
    date_of_birth: Yup.date().required("Date of birth is required"),
    mobile_number: Yup.string().required("Mobile number is required"),
    documentID: Yup.string().required("Document ID is required"),
    nationality: Yup.string().required("Nationality is required"),
    accept_terms: Yup.boolean().required("Accept terms is required"),
    agree_declaration: Yup.boolean().required("Agree declaration is required"),
    road_tax_renewal: Yup.boolean().required("Road tax renewal is required"), // Add this line
  });

  const insuranceProviders = [
    "AIA Berhad",
    "Hong Leong Assurance Berhad",
    "AIA Public Takaful Berhad",
    "Allianz Life Insurance Malaysia Berhad",
    "Manulife Insurance Berhad",
    "Etiqa Family Takaful Berhad",
    "AmMetLife Insurance Berhad",
    "MCIS Insurance Berhad",
    "Hong Leong MSIG Takaful Berhad",
    "Etiqa Life Insurance Berhad",
    "Prudential Assurance Malaysia Berhad",
    "FWD Insurance Berhad",
    "Tokio Marine Life Insurance Malaysia Berhad",
    "Generali Insurance Berhad",
    "Tune Protect Ventures Sdn Bhd (Tune Protect Life)",
    "Great Eastern Life Assurance (Malaysia) Berhad",
    "Zurich Life Insurance Malaysia Berhad",
    "AIA General Berhad",
    "Lonpac Insurance Berhad",
    "AIG Malaysia Insurance Berhad",
    "MSIG Insurance (Malaysia) Berhad",
    "Allianz General Insurance Company (Malaysia) Berhad",
    "Pacific and Orient Insurance Company Berhad",
    "Berjaya Sompo Insurance Berhad",
    "QBE Insurance (Malaysia) Berhad",
    "Chubb Insurance Malaysia Berhad",
    "RHB Insurance Berhad",
    "Etiqa General Insurance Berhad",
    "The Pacific Insurance Berhad",
    "Generali Insurance Berhad",
    "Tokio Marine Insurance (Malaysia) Berhad",
    "Great Eastern General Insurance (Malaysia) Berhad",
    "Tune Insurance Malaysia Berhad",
    "Liberty General Insurance Berhad",
    "Zurich General Insurance Malaysia Berhad",
    "Etiqa General Takaful Berhad",
    "Takaful Ikhlas General Berhad",
    "Investor Trust Assurance",
    "Syarikat Takaful Malaysia Keluarga Berhad",
    "Zurich General Takaful Malaysia Berhad",
  ];

  const insuranceTypeMapping = {
    1: "Foreign Worker",
    2: "Term Life",
    6: "Offshore Insurance",
    7: "Group Insurance",
    8: "Property Insurance",
  };

  const handleSubmit = async (
    values,
    { setSubmitting, setErrors, setFieldTouched }
  ) => {
    setIsSubmittingForm(true);
    try {
      const payload = {
        user_id: values.user_id,
        product_id: values.product_id,
        order_type: values.order_type,
        insurance_type: values.insurance_type,
        enquiry_id: values.enquiry_id,
        order_number: values.order_number,
        full_name: values.full_name,
        username: values.username,
        insurance_plan: values.insurance_plan,
        insurer_name: values.insurer_name,
        order_status: values.order_status,
        order_date: values.order_date,
        order_value: values.order_value,
        payment_status: values.payment_status,
        tracking_number: values.tracking_number,
        order_notes: values.order_notes,
        product_code: values.product_code,
        quote_id: values.quote_id,
        selected_plan_code: values.selected_plan_code,
        quoted_amount: values.quoted_amount,
        state: values.state,
        city: values.city,
        postcode: values.postcode,
        address_line_1: values.address_line_1,
        address_line_2: values.address_line_2,
        document_type: values.document_type,
        gender: values.gender,
        email_address: values.email_address,
        date_of_birth: values.date_of_birth,
        mobile_number: values.mobile_number,
        documentID: values.documentID,
        nationality: values.nationality,
        accept_terms: values.accept_terms,
        agree_declaration: values.agree_declaration,
        road_tax_renewal: values.road_tax_renewal === "true" ? 1 : 0,
        additional_fields: values.additional_fields,
        optional_benefits: values.optional_benefits,
        nominees: values.nominees,
      };
      await createOrder(payload);
      toast.success("Order created successfully!");
      navigate("/orders");
    } catch (error) {
      console.error("Failed to create order:", error);
      toast.error(error.message || "Failed to create order. Please try again.");
      setErrors({ submit: "Failed to create order", ...error.fieldErrors });
      Object.keys(values).forEach((field) =>
        setFieldTouched(field, true, false)
      );
    } finally {
      setSubmitting(false);
      setIsSubmittingForm(false);
    }
  };

  return (
    <>
      {isLoading ? (
        <div className="card border-light">
          <CSpinner color="primary" />
        </div>
      ) : (
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, errors, touched, setFieldValue }) => (
            <Form>
              {isSubmittingForm && (
                <div className="form-overlay">
                  <CSpinner color="primary" />
                </div>
              )}
              <div className="card border-light">
                <div className="card-body">
                  {errors.submit && (
                    <div className="alert alert-danger">{errors.submit}</div>
                  )}
                  {/* Form fields */}
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="user_id" className="form-label">
                        <span className="text-danger">*</span> User ID
                      </label>
                      <Field
                        name="user_id"
                        as="input"
                        type="text"
                        placeholder="Enter user ID"
                        className={`form-control ${
                          touched.user_id && errors.user_id ? "is-invalid" : ""
                        }`}
                      />
                      <ErrorMessage
                        name="user_id"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label htmlFor="insurance_type" className="form-label">
                        <span className="text-danger">*</span> Insurance Type
                      </label>
                      <Field
                        name="insurance_type"
                        as="select"
                        className={`form-select ${
                          touched.insurance_type && errors.insurance_type
                            ? "is-invalid"
                            : ""
                        }`}
                      >
                        <option value="">Select Insurance Type</option>
                        {Object.entries(insuranceTypeMapping).map(
                          ([key, value]) => (
                            <option key={key} value={key}>
                              {value}
                            </option>
                          )
                        )}
                      </Field>
                      <ErrorMessage
                        name="insurance_type"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label htmlFor="enquiry_id" className="form-label">
                        <span className="text-danger">*</span> Enquiry ID
                      </label>
                      <Field
                        name="enquiry_id"
                        as="input"
                        type="text"
                        placeholder="Enter enquiry ID"
                        className={`form-control ${
                          touched.enquiry_id && errors.enquiry_id
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="enquiry_id"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="order_number" className="form-label">
                        <span className="text-danger">*</span> Order Number
                      </label>
                      <Field
                        name="order_number"
                        as="input"
                        type="text"
                        placeholder="Enter order number"
                        className={`form-control ${
                          touched.order_number && errors.order_number
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="order_number"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label htmlFor="full_name" className="form-label">
                        <span className="text-danger">*</span> Full Name
                      </label>
                      <Field
                        name="full_name"
                        as="input"
                        type="text"
                        placeholder="Enter full name"
                        className={`form-control ${
                          touched.full_name && errors.full_name
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="full_name"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="username" className="form-label">
                        <span className="text-danger">*</span> Username
                      </label>
                      <Field
                        name="username"
                        as="input"
                        type="text"
                        placeholder="Enter username"
                        className={`form-control ${
                          touched.username && errors.username
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="username"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label htmlFor="insurance_plan" className="form-label">
                        <span className="text-danger">*</span> Insurance Plan
                      </label>
                      <Field
                        name="insurance_plan"
                        as="input"
                        type="text"
                        placeholder="Enter insurance plan"
                        className={`form-control ${
                          touched.insurance_plan && errors.insurance_plan
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="insurance_plan"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="insurer_name" className="form-label">
                        <span className="text-danger">*</span> Insurer Name
                      </label>
                      <select
                        id="insurer_name"
                        name="insurer_name"
                        className={`form-select ${
                          touched.insurer_name && errors.insurer_name
                            ? "is-invalid"
                            : ""
                        }`}
                        value={insuranceProviderType}
                        onChange={(event) =>
                          handleInsuranceProviderTypeChange(
                            event,
                            setFieldValue
                          )
                        }
                      >
                        <option value="select">Select Insurer Name</option>
                        {insuranceProviders.map((provider, index) => (
                          <option key={index} value={provider}>
                            {provider}
                          </option>
                        ))}
                        <option value="other">Other</option>
                      </select>
                      {insuranceProviderType === "other" && (
                        <Field
                          name="insurer_name"
                          as="input"
                          type="text"
                          className={`form-control mt-2 ${
                            touched.insurer_name && errors.insurer_name
                              ? "is-invalid"
                              : ""
                          }`}
                          placeholder="Enter Insurer Name"
                        />
                      )}
                      <ErrorMessage
                        name="insurer_name"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label htmlFor="order_status" className="form-label">
                        <span className="text-danger">*</span> Order Status
                      </label>
                      <Field
                        name="order_status"
                        as="select"
                        className={`form-select ${
                          touched.order_status && errors.order_status
                            ? "is-invalid"
                            : ""
                        }`}
                      >
                        <option value="">Select Order Status</option>
                        <option value="Pending Quotation">
                          Pending Quotation
                        </option>
                        <option value="Quotation is Ready">
                          Quotation is Ready
                        </option>
                        <option value="Pending Payment">Pending Payment</option>
                        <option value="Payment Success">Payment Success</option>
                        <option value="Active">Active</option>
                        <option value="Expired & Renewal Required">
                          Expired & Renewal Required
                        </option>
                        <option value="Paid">Paid</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Completed">Completed</option>
                      </Field>
                      <ErrorMessage
                        name="order_status"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label htmlFor="order_date" className="form-label">
                        <span className="text-danger">*</span> Order Date
                      </label>
                      <Field
                        name="order_date"
                        as="input"
                        type="date"
                        className={`form-control ${
                          touched.order_date && errors.order_date
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="order_date"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label htmlFor="order_value" className="form-label">
                        <span className="text-danger">*</span> Order Value
                      </label>
                      <Field
                        name="order_value"
                        as="input"
                        type="number"
                        placeholder="Enter order value"
                        className={`form-control ${
                          touched.order_value && errors.order_value
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="order_value"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="payment_status" className="form-label">
                        <span className="text-danger">*</span> Payment Status
                      </label>
                      <Field
                        name="payment_status"
                        as="select"
                        className={`form-select ${
                          touched.payment_status && errors.payment_status
                            ? "is-invalid"
                            : ""
                        }`}
                      >
                        <option value="">Select Payment Status</option>
                        <option value="pending">Pending</option>
                        <option value="completed">Completed</option>
                      </Field>
                      <ErrorMessage
                        name="payment_status"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label htmlFor="tracking_number" className="form-label">
                        <span className="text-danger">*</span> Tracking Number
                      </label>
                      <Field
                        name="tracking_number"
                        as="input"
                        type="text"
                        placeholder="Enter tracking number"
                        className={`form-control ${
                          touched.tracking_number && errors.tracking_number
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="tracking_number"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="order_notes" className="form-label">
                        <span className="text-danger">*</span> Order Notes
                      </label>
                      <Field
                        name="order_notes"
                        as="textarea"
                        placeholder="Enter order notes"
                        className={`form-control ${
                          touched.order_notes && errors.order_notes
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="order_notes"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label htmlFor="product_code" className="form-label">
                        <span className="text-danger">*</span> Product Code
                      </label>
                      <Field
                        name="product_code"
                        as="input"
                        type="text"
                        placeholder="Enter product code"
                        className={`form-control ${
                          touched.product_code && errors.product_code
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="product_code"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="quote_id" className="form-label">
                        <span className="text-danger">*</span> Quote ID
                      </label>
                      <Field
                        name="quote_id"
                        as="input"
                        type="text"
                        placeholder="Enter quote ID"
                        className={`form-control ${
                          touched.quote_id && errors.quote_id
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="quote_id"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label
                        htmlFor="selected_plan_code"
                        className="form-label"
                      >
                        <span className="text-danger">*</span> Selected Plan
                        Code
                      </label>
                      <Field
                        name="selected_plan_code"
                        as="input"
                        type="text"
                        placeholder="Enter selected plan code"
                        className={`form-control ${
                          touched.selected_plan_code &&
                          errors.selected_plan_code
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="selected_plan_code"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="quoted_amount" className="form-label">
                        <span className="text-danger">*</span> Quoted Amount
                      </label>
                      <Field
                        name="quoted_amount"
                        as="input"
                        type="number"
                        placeholder="Enter quoted amount"
                        className={`form-control ${
                          touched.quoted_amount && errors.quoted_amount
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="quoted_amount"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label htmlFor="state" className="form-label">
                        <span className="text-danger">*</span> State
                      </label>
                      <Field
                        name="state"
                        as="input"
                        type="text"
                        placeholder="Enter state"
                        className={`form-control ${
                          touched.state && errors.state ? "is-invalid" : ""
                        }`}
                      />
                      <ErrorMessage
                        name="state"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="city" className="form-label">
                        <span className="text-danger">*</span> City
                      </label>
                      <Field
                        name="city"
                        as="input"
                        type="text"
                        placeholder="Enter city"
                        className={`form-control ${
                          touched.city && errors.city ? "is-invalid" : ""
                        }`}
                      />
                      <ErrorMessage
                        name="city"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label htmlFor="postcode" className="form-label">
                        <span className="text-danger">*</span> Postcode
                      </label>
                      <Field
                        name="postcode"
                        as="input"
                        type="text"
                        placeholder="Enter postcode"
                        className={`form-control ${
                          touched.postcode && errors.postcode
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="postcode"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="address_line_1" className="form-label">
                        <span className="text-danger">*</span> Address Line 1
                      </label>
                      <Field
                        name="address_line_1"
                        as="input"
                        type="text"
                        placeholder="Enter address line 1"
                        className={`form-control ${
                          touched.address_line_1 && errors.address_line_1
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="address_line_1"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label htmlFor="address_line_2" className="form-label">
                        <span className="text-danger">*</span> Address Line 2
                      </label>
                      <Field
                        name="address_line_2"
                        as="input"
                        type="text"
                        placeholder="Enter address line 2"
                        className={`form-control ${
                          touched.address_line_2 && errors.address_line_2
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="address_line_2"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="document_type" className="form-label">
                        <span className="text-danger">*</span> Document Type
                      </label>
                      <Field
                        name="document_type"
                        as="input"
                        type="text"
                        placeholder="Enter document type"
                        className={`form-control ${
                          touched.document_type && errors.document_type
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="document_type"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label htmlFor="gender" className="form-label">
                        <span className="text-danger">*</span> Gender
                      </label>
                      <Field
                        name="gender"
                        as="select"
                        className={`form-control ${
                          touched.gender && errors.gender ? "is-invalid" : ""
                        }`}
                      >
                        <option value="" label="Select gender" />
                        <option value="Male" label="Male" />
                        <option value="Female" label="Female" />
                      </Field>
                      <ErrorMessage
                        name="gender"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="email_address" className="form-label">
                        <span className="text-danger">*</span> Email Address
                      </label>
                      <Field
                        name="email_address"
                        as="input"
                        type="email"
                        placeholder="Enter email address"
                        className={`form-control ${
                          touched.email_address && errors.email_address
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="email_address"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label htmlFor="date_of_birth" className="form-label">
                        <span className="text-danger">*</span> Date of Birth
                      </label>
                      <Field
                        name="date_of_birth"
                        as="input"
                        type="date"
                        className={`form-control ${
                          touched.date_of_birth && errors.date_of_birth
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="date_of_birth"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="mobile_number" className="form-label">
                        <span className="text-danger">*</span> Mobile Number
                      </label>
                      <Field
                        name="mobile_number"
                        as="input"
                        type="text"
                        placeholder="Enter mobile number"
                        className={`form-control ${
                          touched.mobile_number && errors.mobile_number
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="mobile_number"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label htmlFor="documentID" className="form-label">
                        <span className="text-danger">*</span> Document ID
                      </label>
                      <Field
                        name="documentID"
                        as="input"
                        type="text"
                        placeholder="Enter document ID"
                        className={`form-control ${
                          touched.documentID && errors.documentID
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="documentID"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="nationality" className="form-label">
                        <span className="text-danger">*</span> Nationality
                      </label>
                      <Field
                        name="nationality"
                        as="input"
                        type="text"
                        placeholder="Enter nationality"
                        className={`form-control ${
                          touched.nationality && errors.nationality
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="nationality"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label htmlFor="accept_terms" className="form-label">
                        <span className="text-danger">*</span> Accept Terms
                      </label>
                      <Field
                        name="accept_terms"
                        as="select"
                        className={`form-select ${
                          touched.accept_terms && errors.accept_terms
                            ? "is-invalid"
                            : ""
                        }`}
                      >
                        <option value="">Select Accept Terms</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </Field>
                      <ErrorMessage
                        name="accept_terms"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="agree_declaration" className="form-label">
                        <span className="text-danger">*</span> Agree Declaration
                      </label>
                      <Field
                        name="agree_declaration"
                        as="select"
                        className={`form-select ${
                          touched.agree_declaration && errors.agree_declaration
                            ? "is-invalid"
                            : ""
                        }`}
                      >
                        <option value="">Select Agree Declaration</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </Field>
                      <ErrorMessage
                        name="agree_declaration"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label htmlFor="road_tax_renewal" className="form-label">
                        <span className="text-danger">*</span> Road Tax Renewal
                      </label>
                      <Field
                        name="road_tax_renewal"
                        as="select"
                        className={`form-select ${
                          touched.road_tax_renewal && errors.road_tax_renewal
                            ? "is-invalid"
                            : ""
                        }`}
                      >
                        <option value="">Select Road Tax Renewal</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </Field>
                      <ErrorMessage
                        name="road_tax_renewal"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                  </div>

                  <div className="col-md-12 mb-3">
                    <label htmlFor="additional_fields" className="form-label">
                      Additional Fields
                    </label>
                    <FieldArray name="additional_fields">
                      {({ push, remove, form }) => (
                        <>
                          {form.values.additional_fields.length === 0 && (
                            <div className="d-flex justify-content-start mb-2">
                              <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => push({ name: "", value: "" })}
                              >
                                Add Field
                              </button>
                            </div>
                          )}
                          {form.values.additional_fields.map((field, index) => (
                            <div key={index} className="d-flex mb-2">
                              <Field
                                name={`additional_fields.${index}.name`}
                                as="input"
                                type="text"
                                placeholder="Field name"
                                className="form-control me-2"
                              />
                              <Field
                                name={`additional_fields.${index}.value`}
                                as="input"
                                type="text"
                                placeholder="Field value"
                                className="form-control me-2"
                              />
                              <button
                                type="button"
                                className="btn btn-danger"
                                onClick={() => remove(index)}
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                          {form.values.additional_fields.length > 0 && (
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={() => push({ name: "", value: "" })}
                            >
                              Add Field
                            </button>
                          )}
                        </>
                      )}
                    </FieldArray>
                  </div>

                  <div className="col-md-12 mb-3">
                    <label htmlFor="optional_benefits" className="form-label">
                      Optional Benefits
                    </label>
                    <FieldArray name="optional_benefits">
                      {({ push, remove, form }) => (
                        <>
                          {form.values.optional_benefits.length === 0 && (
                            <div className="d-flex justify-content-start mb-2">
                              <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() =>
                                  push({
                                    applicable_amount: "",
                                    benefit: "",
                                    sum_insured: "",
                                  })
                                }
                              >
                                Add Benefit
                              </button>
                            </div>
                          )}
                          {form.values.optional_benefits.map(
                            (benefit, index) => (
                              <div key={index} className="d-flex mb-2">
                                <Field
                                  name={`optional_benefits.${index}.applicable_amount`}
                                  as="input"
                                  type="text"
                                  placeholder="Applicable amount"
                                  className="form-control me-2"
                                />
                                <Field
                                  name={`optional_benefits.${index}.benefit`}
                                  as="input"
                                  type="text"
                                  placeholder="Benefit"
                                  className="form-control me-2"
                                />
                                <Field
                                  name={`optional_benefits.${index}.sum_insured`}
                                  as="input"
                                  type="text"
                                  placeholder="Sum insured"
                                  className="form-control me-2"
                                />
                                <button
                                  type="button"
                                  className="btn btn-danger"
                                  onClick={() => remove(index)}
                                >
                                  Remove
                                </button>
                              </div>
                            )
                          )}
                          {form.values.optional_benefits.length > 0 && (
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={() =>
                                push({
                                  applicable_amount: "",
                                  benefit: "",
                                  sum_insured: "",
                                })
                              }
                            >
                              Add Benefit
                            </button>
                          )}
                        </>
                      )}
                    </FieldArray>
                  </div>

                  <div className="col-md-12 mb-3">
                    <label htmlFor="nominees" className="form-label">
                      Nominees
                    </label>
                    <FieldArray name="nominees">
                      {({ push, remove, form }) => (
                        <>
                          {form.values.nominees.length === 0 && (
                            <div className="d-flex justify-content-start mb-2">
                              <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() =>
                                  push({
                                    nominee_name: "",
                                    nominee_ic: "",
                                    nominee_dob: "",
                                    nominee_age: "",
                                    relationship: "",
                                    nominee_share: "",
                                  })
                                }
                              >
                                Add Nominee
                              </button>
                            </div>
                          )}
                          {form.values.nominees.map((nominee, index) => (
                            <div key={index} className="row mb-2">
                              <div className="col-md-6">
                                <Field
                                  name={`nominees.${index}.nominee_name`}
                                  as="input"
                                  type="text"
                                  placeholder="Nominee name"
                                  className="form-control mb-2"
                                />
                                <Field
                                  name={`nominees.${index}.nominee_ic`}
                                  as="input"
                                  type="text"
                                  placeholder="Nominee IC"
                                  className="form-control mb-2"
                                />
                                <Field
                                  name={`nominees.${index}.nominee_dob`}
                                  as="input"
                                  type="date"
                                  className="form-control mb-2"
                                />
                              </div>
                              <div className="col-md-6">
                                <Field
                                  name={`nominees.${index}.nominee_age`}
                                  as="input"
                                  type="number"
                                  placeholder="Nominee age"
                                  className="form-control mb-2"
                                />
                                <Field
                                  name={`nominees.${index}.relationship`}
                                  as="input"
                                  type="text"
                                  placeholder="Relationship"
                                  className="form-control mb-2"
                                />
                                <div className="d-flex">
                                  <Field
                                    name={`nominees.${index}.nominee_share`}
                                    as="input"
                                    type="number"
                                    placeholder="Nominee share"
                                    className="form-control me-2"
                                  />
                                  <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={() => remove(index)}
                                  >
                                    Remove
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                          {form.values.nominees.length > 0 && (
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={() =>
                                push({
                                  nominee_name: "",
                                  nominee_ic: "",
                                  nominee_dob: "",
                                  nominee_age: "",
                                  relationship: "",
                                  nominee_share: "",
                                })
                              }
                            >
                              Add Nominee
                            </button>
                          )}
                        </>
                      )}
                    </FieldArray>
                  </div>

                  <div className="d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn me-3 ansa-button-secondary"
                      onClick={() => navigate("/orders")}
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      className="btn ansa-button-primary"
                      disabled={isSubmitting}
                    >
                      Create Order
                    </button>
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </>
  );
};

export default CreateOrder;
